import * as React from 'react';
import { Box, Card, Stack, Typography } from '@mui/material';
import { ListBase, Pagination, useCanAccess, useShowContext } from 'react-admin';

import { EditProjectNumberDialog } from '@/modules/projects/EditProjectNumberDialog';
import { RelatedPeopleTable } from '@/modules/projects/RelatedPeopleTable';
import { EditProjectContactsDialog } from '@/modules/quotes/EditProjectContractorDialog';
import { JobFolderHeader } from '@/modules/quotes/JobFolderHeader';
import { QuoteListContent } from '@/modules/quotes/QuoteListContent';
import { QuoteJobFolder } from '@/types';
import { CompleteProjectDialog } from './CompleteProjectDialog';
import { ReopenQuotingDialog } from './ReopenQuotingDialog';

export const ProjectSummaryTab = () => {
    const { record, refetch } = useShowContext<QuoteJobFolder>();
    const [editOpen, setEditOpen] = React.useState(false);
    const [editContactsOpen, setEditContactsOpen] = React.useState(false);
    const [completeOpen, setCompleteOpen] = React.useState(false);
    const [reopenQuotingOpen, setReopenQuotingOpen] = React.useState(false);
    const { canAccess: canCompleteProject } = useCanAccess({
        resource: 'project',
        action: 'complete',
    });
    const { canAccess: canReopenQuoting } = useCanAccess({
        resource: 'project',
        action: 'reopen_quoting',
    });
    if (!record) return null;

    const canShowComplete =
        record.phase === 'active' && canCompleteProject && !record.has_legacy;
    const canShowReopenQuoting =
        record.phase === 'active' && canReopenQuoting;

    return (
        <Stack gap={2}>
            <JobFolderHeader
                folder={record}
                listPath="/projects"
                onEditProjectNumber={
                    record.project_no != null && !record.has_legacy
                        ? () => setEditOpen(true)
                        : undefined
                }
                onEditContacts={() => setEditContactsOpen(true)}
                onCompleteProject={
                    canShowComplete ? () => setCompleteOpen(true) : undefined
                }
                onReopenQuoting={
                    canShowReopenQuoting
                        ? () => setReopenQuotingOpen(true)
                        : undefined
                }
            />
            <CompleteProjectDialog
                open={completeOpen}
                projectId={Number(record.id)}
                onClose={() => setCompleteOpen(false)}
                onCompleted={() => refetch?.()}
            />
            <ReopenQuotingDialog
                open={reopenQuotingOpen}
                projectId={Number(record.id)}
                onClose={() => setReopenQuotingOpen(false)}
            />
            {record.project_no != null && !record.has_legacy && (
                <EditProjectNumberDialog
                    open={editOpen}
                    projectId={record.id}
                    currentProjectNo={record.project_no}
                    onClose={() => setEditOpen(false)}
                />
            )}
            <EditProjectContactsDialog
                open={editContactsOpen}
                folder={record}
                onClose={() => setEditContactsOpen(false)}
            />
            <RelatedPeopleTable projectId={Number(record.id)} />
            <Box>
                <Typography variant="subtitle1" sx={{ px: 1, mb: 1 }}>
                    Quotes
                </Typography>
                <ListBase
                    disableAuthentication
                    resource="quotes"
                    filter={{ project_id: record.id }}
                    sort={{ field: 'id', order: 'DESC' }}
                    perPage={25}
                >
                    <Box>
                        <Card>
                            <QuoteListContent
                                linkPrefix="/quotes"
                                selectable={false}
                                showProjectSummary={false}
                            />
                        </Card>
                        <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
                    </Box>
                </ListBase>
            </Box>
        </Stack>
    );
};
